import { Fade, Icon, useMediaQuery } from '@material-ui/core';
import Button, { ButtonProps } from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import ListItemText from '@material-ui/core/ListItemText';
import Menu, { MenuProps } from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import RemoveIcon from '@material-ui/icons/Remove';
import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import colors from '../../themes/main-colors';
import { Group } from '../../__generated__/pge-types';

const StyledButton = ({
  isDisplay,
  ...rest
}: ButtonProps & { isDisplay: boolean }) => (
  <Button
    fullWidth
    aria-controls="customized-menu"
    aria-haspopup="true"
    variant="outlined"
    endIcon={isDisplay ? undefined : <KeyboardArrowDownIcon />}
    style={{
      background: '#fff',
    }}
    disableRipple={isDisplay}
    {...rest}
  />
);

interface StyledMenuProps extends MenuProps {
  width: number;
}

const useStyledMenuStyles = makeStyles(() => ({
  paper: {
    width: (props: { width: number }) => props.width,
  },
}));

const useStyles = makeStyles((theme: Theme) => ({
  menuItem: {
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    color: colors.noirBlur,
    '& .PrimaryText': {
      flex: '1 0 auto',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    '& .primary': {
      color: colors.noirBlur,
      '& span': {
        whiteSpace: 'normal',
      },
    },
    '& .secondary': {
      color: colors.noirBlur,
      marginRight: theme.spacing(8),
    },
  },
  defaultCursor: {
    cursor: 'default',
  },
  groupContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  groupSelector: {
    flex: '1 1 100%',
  },
  groupTitle: {
    flex: '1 0 auto',
  },
  groupGap: {
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(1),
    },
  },
}));

const StyledMenu = (props: StyledMenuProps) => {
  const classes = useStyledMenuStyles(props);
  return (
    <Menu
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      classes={classes}
      {...props}
    />
  );
};

type Props = {
  selectedGroupId: string;
  groups: Array<Group>;
  onSelect: (groupId: string) => void;
  isDisplay?: boolean | number;
};

export default function AccountGroupDropdown({
  selectedGroupId,
  groups,
  onSelect,
  isDisplay: isDisplayProp = false,
}: Props) {
  const { t } = useTranslation();
  const anchorEl = React.useRef<null | HTMLElement>(null);
  const [width, setWidth] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const isSmallDevice = useMediaQuery('(min-width:768px)');
  const isDisplay =
    isDisplayProp === true ||
    typeof isDisplayProp === 'number' ||
    groups.length === 1;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setWidth(event.currentTarget.clientWidth);
    anchorEl.current = event.currentTarget;
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const handleSelect = (group: Group) => {
    onSelect(group.groupId);
    onClose();
  };
  const classes = useStyles();
  const defaultGroup = () => groups.find(grp => grp.isDefault)!;
  const selectedGroup =
    groups.find(grp => grp.groupId === selectedGroupId) || defaultGroup();

  return (
    <>
      <Grid
        data-noprint
        container
        spacing={1}
        direction="row"
        className={classes.groupContainer}
        wrap={isSmallDevice ? 'nowrap' : 'wrap'}
      >
        <Grid item className={classes.groupTitle}>
          <Typography variant="body1">{t('ACCOUNT_GROUP_LABEL')}</Typography>
        </Grid>
        <Grid item className={classes.groupSelector}>
          <StyledButton
            className={isDisplay ? classes.defaultCursor : undefined}
            onClick={isDisplay ? undefined : handleClick}
            isDisplay={isDisplay}
          >
            <div className={classes.menuItem}>
              <ListItemText
                primary={selectedGroup.groupName}
                className={`primary ${classes.groupGap}`}
                primaryTypographyProps={{
                  style: {
                    fontWeight: 'bold',
                    color: colors.noirBlur,
                    wordBreak: 'break-word',
                  },
                }}
              />
              <ListItemText
                secondary={
                  Boolean(selectedGroup.numberOfAccounts) ? (
                    <Fade in={true} timeout={2000}>
                      <div>
                        {`${
                          typeof isDisplayProp === 'number'
                            ? isDisplayProp
                            : selectedGroup.numberOfAccounts
                        } ${t('ACCOUNTS')}`}
                      </div>
                    </Fade>
                  ) : (
                    <RemoveIcon color="disabled" style={{ marginTop: '5px' }} />
                  )
                }
                className="secondary"
                style={{ flex: '0 0 auto' }}
                secondaryTypographyProps={{
                  style: {
                    color: colors.noirBlur,
                  },
                }}
              />
            </div>
          </StyledButton>
        </Grid>
      </Grid>

      <StyledMenu
        width={width}
        anchorEl={anchorEl.current}
        keepMounted
        open={open}
        onClose={onClose}
      >
        {groups.map(group => (
          <MenuItem
            className={classes.menuItem}
            key={group.groupId}
            selected={group.groupId === selectedGroup.groupId}
            onClick={() => handleSelect(group)}
          >
            <ListItemText
              primary={group.groupName}
              className={`primary ${classes.groupGap}`}
              primaryTypographyProps={{
                style: {
                  fontWeight: 'bold',
                  color: colors.noirBlur,
                  wordBreak: 'break-word',
                },
              }}
            />

            <ListItemText
              secondary={`${group.numberOfAccounts} ${t('ACCOUNTS')}`}
              className="secondary"
              style={{ flex: '0 0 auto' }}
              secondaryTypographyProps={{
                style: {
                  color: colors.noirBlur,
                },
              }}
            />
          </MenuItem>
        ))}
      </StyledMenu>
    </>
  );
}
